/**
 * Чи можна сюди дійти: a straight line walked the way a foe walks it.
 *
 * Orcs have no pathfinder - they lean at whatever they are after and climb
 * what they can. So "can they reach the island" is not a question about
 * heights, it is a question about a walk, and it has to be asked as one:
 * comparing the ground at each step says nothing about whether the floor
 * under a hill can be *got to*, and the first version of this went happily
 * through a mountain by way of a cave.
 */
export interface WalkRules {
  /** Up this many blocks in one step. A foe steps one; nobody climbs. */
  readonly climb: number;
  /** And down this far without it counting as a fall that stops them. */
  readonly drop: number;
}

export const FOOT_RULES: WalkRules = { climb: 1, drop: 3 };

export interface Walk {
  readonly ok: boolean;
  /** Where it stopped, when it stopped: `"x,z from y=n"`. */
  readonly stuck: string;
}

export function walkLine(
  isSolid: (x: number, y: number, z: number) => boolean,
  from: { readonly x: number; readonly z: number },
  to: { readonly x: number; readonly z: number },
  startY: number,
  rules: WalkRules = FOOT_RULES,
): Walk {
  const steps = Math.ceil(Math.hypot(to.x - from.x, to.z - from.z));
  // Two clear cells with something under them: a body standing up.
  const room = (x: number, y: number, z: number): boolean =>
    isSolid(x, y - 1, z) && !isSolid(x, y, z) && !isSolid(x, y + 1, z);
  const steppable: number[] = [];
  for (let up = rules.climb; up >= -rules.drop; up--) steppable.push(up);

  let level = startY;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const x = Math.round(from.x + (to.x - from.x) * t);
    const z = Math.round(from.z + (to.z - from.z) * t);
    const found = steppable
      .map((step) => level + step)
      .find((y) => room(x, y, z));
    if (found === undefined) return { ok: false, stuck: `${x},${z} from y=${level}` };
    level = found;
  }
  return { ok: true, stuck: "" };
}

/**
 * Where the road to the кремль stops, in blocks west of the fortress's middle.
 *
 * Thirty blocks west of the middle is the first column outside the front
 * face, which is where the road stops and the gate begins.
 *
 * It was twenty-six, which is *inside* the fortress's own footprint - the
 * road ran at the wall - and it was chosen by measuring one seed. On the
 * other eleven of twelve the last stretch was a cliff. The road now carries
 * its own ramp: it is given the gate's sill and told to arrive at it, and
 * the one-course-a-step clamp does the rest.
 */
export const ROAD_STOPS_SHORT = 30;

/**
 * And the gate's own mouth, where the walk is asked to reach.
 *
 * Twenty-five blocks west of the middle, which is four blocks inside the
 * face: through the arch and standing in the passage, rather than in front
 * of it.
 */
/**
 * How deep a road's footing goes looking for something to stand on.
 *
 * Deep enough for any hillside this terrain makes, and shallow enough that a
 * road over open water is a pier and not an earthwork across the strait. The
 * fill stops the moment it meets solid ground, so this number only matters
 * where there is nothing under the road at all.
 */
/**
 * How far above a road the cutting is opened.
 *
 * A walker needs three; this is not about the walker. It is how deep a bank
 * the road may cut through and still leave sky over it, and on these domes
 * the steepest stretch is about a dozen.
 */
export const ROAD_HEADROOM = 24;

export const ROAD_FOOTING = 24;

export const GATE_MOUTH = 25;
